import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { adminApi } from "@/lib/adminAuth";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const MODELS = {
  openai: ["gpt-5.4", "gpt-5.4-mini", "gpt-5.2", "gpt-5", "gpt-4o"],
  anthropic: ["claude-sonnet-4-6", "claude-opus-4-8", "claude-haiku-4-5-20251001"],
  gemini: ["gemini-3-flash-preview", "gemini-3.1-pro-preview", "gemini-3.5-flash"],
};

export default function AdminFortuneTypes() {
  const [types, setTypes] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = () => api.get("/admin/fortune-types", adminApi()).then(({ data }) => setTypes(data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (creating) {
        await api.post("/admin/fortune-types", editing, adminApi());
      } else {
        await api.patch(`/admin/fortune-types/${editing.id}`, editing, adminApi());
      }
      toast.success("Kaydedildi");
      setEditing(null); setCreating(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Silmek istediğine emin misin?")) return;
    await api.delete(`/admin/fortune-types/${id}`, adminApi());
    load();
  };

  const startCreate = () => {
    setCreating(true);
    setEditing({
      slug: "", name_tr: "", name_en: "", description: "", icon: "Sparkles",
      cover_image: "", requires_photo: false, requires_text: false, requires_cards: false,
      requires_two_names: false, requires_zodiac: false, requires_dream_school: false,
      token_cost: 10, wait_seconds: 8, system_prompt: "", forbidden_words: [], required_phrases: [],
      is_active: true, sort_order: 100, provider: "openai", model: "gpt-5.4-mini",
    });
  };

  return (
    <div className="font-admin-body">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-admin-head text-3xl">Fal Türleri</h1>
        <button onClick={startCreate} data-testid="create-fortune-type-btn" className="admin-btn flex items-center gap-2"><Plus className="w-4 h-4" /> Yeni Fal Türü</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {types.map((t) => (
          <div key={t.id} data-testid={`ft-${t.slug}`} className="admin-card p-4">
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="font-admin-head text-lg">{t.name_tr}</div>
                <div className="text-xs text-white/50">/{t.slug} · {t.provider}/{t.model}</div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => setEditing({ ...t })} data-testid={`edit-ft-${t.slug}`} className="admin-btn-ghost text-xs px-2 py-1">Düzenle</button>
                <button onClick={() => remove(t.id)} className="admin-btn-ghost text-xs px-2 py-1 text-red-400"><Trash2 className="w-3 h-3" /></button>
              </div>
            </div>
            <div className="text-xs text-white/60 line-clamp-2 mb-2">{t.description}</div>
            <div className="flex flex-wrap gap-1 text-xs">
              <span className="bg-white/5 px-2 py-0.5 rounded">{t.token_cost} jeton</span>
              {t.requires_photo && <span className="bg-white/5 px-2 py-0.5 rounded">foto</span>}
              {t.requires_text && <span className="bg-white/5 px-2 py-0.5 rounded">metin</span>}
              {t.requires_cards && <span className="bg-white/5 px-2 py-0.5 rounded">kart</span>}
              {t.is_active ? <span className="bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded">Aktif</span> : <span className="bg-red-500/20 text-red-300 px-2 py-0.5 rounded">Pasif</span>}
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4 overflow-y-auto" data-testid="edit-ft-modal">
          <div className="admin-card p-6 w-full max-w-2xl my-8">
            <h2 className="font-admin-head text-xl mb-4">{creating ? "Yeni Fal Türü" : "Fal Türünü Düzenle"}</h2>
            <div className="grid grid-cols-2 gap-3">
              {creating && (
                <div>
                  <label className="text-xs text-white/50">Slug (URL)</label>
                  <input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} className="admin-input" />
                </div>
              )}
              <div>
                <label className="text-xs text-white/50">Ad (TR)</label>
                <input value={editing.name_tr} onChange={(e) => setEditing({ ...editing, name_tr: e.target.value })} data-testid="ft-name-input" className="admin-input" />
              </div>
              <div>
                <label className="text-xs text-white/50">İkon (lucide)</label>
                <input value={editing.icon} onChange={(e) => setEditing({ ...editing, icon: e.target.value })} className="admin-input" />
              </div>
              <div>
                <label className="text-xs text-white/50">Kapak URL</label>
                <input value={editing.cover_image || ""} onChange={(e) => setEditing({ ...editing, cover_image: e.target.value })} className="admin-input" />
              </div>
              <div className="col-span-2">
                <label className="text-xs text-white/50">Açıklama</label>
                <input value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="admin-input" />
              </div>
              <div>
                <label className="text-xs text-white/50">Jeton Ücreti</label>
                <input type="number" value={editing.token_cost} onChange={(e) => setEditing({ ...editing, token_cost: parseInt(e.target.value) || 0 })} data-testid="ft-cost-input" className="admin-input" />
              </div>
              <div>
                <label className="text-xs text-white/50">Bekleme (sn)</label>
                <input type="number" value={editing.wait_seconds} onChange={(e) => setEditing({ ...editing, wait_seconds: parseInt(e.target.value) || 0 })} className="admin-input" />
              </div>
              <div>
                <label className="text-xs text-white/50">AI Sağlayıcı</label>
                <select value={editing.provider} onChange={(e) => setEditing({ ...editing, provider: e.target.value, model: MODELS[e.target.value][0] })} className="admin-input">
                  {Object.keys(MODELS).map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-white/50">Model</label>
                <select value={editing.model} onChange={(e) => setEditing({ ...editing, model: e.target.value })} className="admin-input">
                  {(MODELS[editing.provider] || []).map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div className="col-span-2 grid grid-cols-3 gap-2 text-sm">
                <label className="flex items-center gap-2"><input type="checkbox" checked={!!editing.requires_photo} onChange={(e) => setEditing({ ...editing, requires_photo: e.target.checked })} className="accent-[#F59E0B]" /> Fotoğraf</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={!!editing.requires_text} onChange={(e) => setEditing({ ...editing, requires_text: e.target.checked })} className="accent-[#F59E0B]" /> Metin</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={!!editing.requires_cards} onChange={(e) => setEditing({ ...editing, requires_cards: e.target.checked })} className="accent-[#F59E0B]" /> Kart</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={!!editing.requires_two_names} onChange={(e) => setEditing({ ...editing, requires_two_names: e.target.checked })} className="accent-[#F59E0B]" /> 2 İsim</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={!!editing.requires_zodiac} onChange={(e) => setEditing({ ...editing, requires_zodiac: e.target.checked })} className="accent-[#F59E0B]" /> Burç</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={!!editing.requires_dream_school} onChange={(e) => setEditing({ ...editing, requires_dream_school: e.target.checked })} className="accent-[#F59E0B]" /> Rüya Ekolü</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={!!editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} className="accent-[#F59E0B]" /> Aktif</label>
              </div>
              <div className="col-span-2">
                <label className="text-xs text-white/50">System Prompt (AI'yı eğit: yapması/yapmaması gerekenler)</label>
                <textarea value={editing.system_prompt} onChange={(e) => setEditing({ ...editing, system_prompt: e.target.value })} rows={6} data-testid="ft-prompt-input" className="admin-input font-mono text-xs" />
              </div>
              <div className="col-span-2">
                <label className="text-xs text-white/50">Yasak Kelimeler (virgülle ayır)</label>
                <input value={(editing.forbidden_words || []).join(", ")} onChange={(e) => setEditing({ ...editing, forbidden_words: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} className="admin-input" />
              </div>
              <div className="col-span-2">
                <label className="text-xs text-white/50">Zorunlu İfadeler (virgülle ayır)</label>
                <input value={(editing.required_phrases || []).join(", ")} onChange={(e) => setEditing({ ...editing, required_phrases: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} className="admin-input" />
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <button onClick={save} data-testid="save-ft-btn" className="admin-btn flex-1">Kaydet</button>
              <button onClick={() => { setEditing(null); setCreating(false); }} className="admin-btn-ghost flex-1">İptal</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
